import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import API_URL from '../config/api';
import { getAdminAuthHeader } from '../utils/adminAuth';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function GameScraper() {
  const location = useLocation();
  const [source, setSource] = useState('f95'); // Default to F95
  const [itchUrl, setItchUrl] = useState('');
  const [f95Url, setF95Url] = useState('');
  const [scraping, setScraping] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [success, setSuccess] = useState(null);
  const [tags, setTags] = useState([]);
  const [editableScreenshots, setEditableScreenshots] = useState([]);
  
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  useEffect(() => {
    fetchTags();
  }, []);

  // Check URL params for pre-filled links
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const itchParam = params.get('itchUrl');
    const f95Param = params.get('f95Url');
    const sourceParam = params.get('source');
    
    if (itchParam) {
      setItchUrl(itchParam);
    }
    if (f95Param) {
      setF95Url(f95Param);
    }
    if (sourceParam && (sourceParam === 'itch' || sourceParam === 'f95')) {
      setSource(sourceParam);
    }
  }, [location.search]);
  
  // Initialize editable screenshots when preview data is set
  useEffect(() => {
    if (previewData) {
      const screenshots = previewData.originalScreenshots || previewData.screenshots || [];
      setEditableScreenshots(screenshots);
    }
  }, [previewData]);
  
  const fetchTags = async () => {
    try {
      const response = await fetch(`${API_URL}/tags`);
      if (response.ok) {
        const data = await response.json();
        setTags(data);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };
  
  const getTagName = (tagId) => {
    const tag = tags.find(t => t.id === tagId);
    return tag ? tag.name : `Tag #${tagId}`;
  };
  
  const getTagColor = (tagId) => {
    const tag = tags.find(t => t.id === tagId);
    return tag ? tag.color : '#3498db';
  };
  
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      setEditableScreenshots((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };
  
  const handleRemoveScreenshot = (index) => {
    setEditableScreenshots((items) => items.filter((_, i) => i !== index));
  };

  const handleScrape = async () => {
    if (!itchUrl && !f95Url) {
      setError('Please provide at least one URL (Itch.io or F95Zone)');
      return;
    }

    try {
      setScraping(true);
      setError(null);
      setSuccess(null);
      setPreviewData(null);

      const authHeader = getAdminAuthHeader();
      const response = await fetch(`${API_URL}/admin/scrape-game`, {
        method: 'POST',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          source: source,
          itchUrl: itchUrl || null,
          f95Url: f95Url || null,
        }),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to scrape game data';
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
          } else {
            const text = await response.text();
            errorMessage = text || errorMessage;
          }
        } catch (parseError) {
          console.error('Error parsing error response:', parseError);
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setPreviewData(data);
    } catch (err) {
      console.error('Error scraping game:', err);
      setError(err.message || 'Failed to scrape game data');
    } finally {
      setScraping(false);
    }
  };

  const handleConfirm = async () => {
    if (!previewData || !previewData.gameObject) {
      setError('No game data to confirm');
      return;
    }

    try {
      setConfirming(true);
      setError(null);
      setSuccess(null);

      const authHeader = getAdminAuthHeader();
      
      // Use the first image as cover, rest sent via originalScreenshots
      const coverImage = editableScreenshots.length > 0 ? editableScreenshots[0] : null;

      const requestBody = {
        gameObject: previewData.gameObject,
        creator: previewData.creator,
        downloadImages: true,
        originalScreenshots: editableScreenshots, // Send all images (first is cover, rest are screenshots)
        originalCoverImage: coverImage, // Explicitly set cover to first image
      };
      
      
      const response = await fetch(`${API_URL}/admin/confirm-game`, {
        method: 'POST',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to save game';
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
          } else {
            const text = await response.text();
            errorMessage = text || errorMessage;
          }
        } catch (parseError) {
          console.error('Error parsing error response:', parseError);
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      setSuccess(`Game added successfully! Game ID: ${result.gameId}`);
      setPreviewData(null);
      setItchUrl('');
      setF95Url('');
    } catch (err) {
      console.error('Error confirming game:', err);
      setError(err.message || 'Failed to save game');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white mb-1">Game Scraper</h2>
        <div className="text-white/40 text-xs uppercase tracking-wider">
          Scrape game data from Itch.io and F95Zone
        </div>
      </div>

      {/* Input Form */}
      <div className="bg-dark-card border border-dark-border rounded-lg p-6">
        <div className="space-y-4">
          <div>
            <label className="block text-white/60 text-sm font-medium mb-2">
              Itch.io URL
            </label>
            <input
              type="text"
              value={itchUrl}
              onChange={(e) => setItchUrl(e.target.value)}
              placeholder="https://creator.itch.io/game-name"
              className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-accent-blue/50 transition-colors"
            />
          </div>

          <div>
            <label className="block text-white/60 text-sm font-medium mb-2">
              F95Zone URL (Optional)
            </label>
            <input
              type="text"
              value={f95Url}
              onChange={(e) => setF95Url(e.target.value)}
              placeholder="https://f95zone.to/threads/game-name.12345/"
              className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-accent-blue/50 transition-colors"
            />
            <div className="text-white/40 text-xs mt-1">
              F95Zone screenshots will be prioritized for higher quality
            </div>
          </div>

          {/* Source Switch */}
          <div className="flex items-center justify-between pt-2 border-t border-dark-border">
            <div>
              <label className="block text-white/60 text-sm font-medium mb-1">
                Image Source Priority
              </label>
              <div className="text-white/40 text-xs">
                Choose which source to prioritize for screenshots
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-sm ${source === 'itch' ? 'text-white' : 'text-white/40'}`}>Itch.io</span>
              <button
                type="button"
                onClick={() => setSource(source === 'f95' ? 'itch' : 'f95')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  source === 'f95' ? 'bg-accent-blue' : 'bg-white/20'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    source === 'f95' ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`text-sm ${source === 'f95' ? 'text-white' : 'text-white/40'}`}>F95Zone</span>
            </div>
          </div>

          <button
            onClick={handleScrape}
            disabled={scraping}
            className="w-full px-6 py-3 bg-accent-blue/20 border border-accent-blue/30 rounded-xl text-accent-blue font-medium hover:bg-accent-blue/30 hover:border-accent-blue/40 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {scraping ? 'Scraping...' : 'Scrape Game Data'}
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400">
          {error}
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 text-green-400">
          {success}
        </div>
      )}

      {/* Preview Data */}
      {previewData && (
        <div className="space-y-6">
          {/* Debug Info */}
          {process.env.NODE_ENV === 'development' && (
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-blue-400 text-xs">
              <div>Itch Data: {previewData.itchData ? '✅' : '❌'}</div>
              <div>F95 Data: {previewData.f95Data ? '✅' : '❌'}</div>
              <div>Creator: {previewData.creator ? '✅' : '❌'}</div>
              <div>Game Object: {previewData.gameObject ? '✅' : '❌'}</div>
              <div>Screenshots: {previewData.originalScreenshots?.length || previewData.screenshots?.length || 0}</div>
            </div>
          )}
          
          {/* Game Information */}
          <div className="bg-dark-card border border-dark-border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Game Information</h3>
            
            {previewData.gameObject ? (
              <div className="space-y-4">
                <div>
                  <div className="text-white/60 text-sm mb-1">Title</div>
                  <div className="text-white text-lg font-semibold">{previewData.gameObject.title}</div>
                </div>

                <div>
                  <div className="text-white/60 text-sm mb-1">Developer</div>
                  <div className="text-white">{previewData.gameObject.developer}</div>
                </div>

                <div>
                  <div className="text-white/60 text-sm mb-1">Description</div>
                  <div className="text-white/80 text-sm max-h-32 overflow-y-auto">
                    {previewData.gameObject.description}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-white/60 text-sm mb-1">Status</div>
                    <div className="text-white">{previewData.gameObject.properties?.status || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-white/60 text-sm mb-1">Version</div>
                    <div className="text-white">{previewData.gameObject.properties?.version || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-white/60 text-sm mb-1">Size</div>
                    <div className="text-white">{previewData.gameObject.properties?.size || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-white/60 text-sm mb-1">Engine</div>
                    <div className="text-white">{previewData.gameObject.properties?.gameEngine || 'N/A'}</div>
                  </div>
                </div>

                <div>
                  <div className="text-white/60 text-sm mb-2">Tags ({previewData.tagIds?.length || 0})</div>
                  <div className="flex flex-wrap gap-2">
                    {previewData.tagIds?.map((tagId) => {
                      const tagName = getTagName(tagId);
                      const tagColor = getTagColor(tagId);
                      return (
                        <span
                          key={tagId}
                          className="px-2 py-1 rounded text-xs font-medium"
                          style={{
                            backgroundColor: `${tagColor}15`,
                            border: `1px solid ${tagColor}40`,
                            color: tagColor
                          }}
                        >
                          {tagName}
                        </span>
                      );
                    })}
                  </div>
                </div>

                {previewData.validation && !previewData.validation.valid && (
                  <div className="mt-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                    <div className="text-yellow-400 text-sm font-medium mb-2">Validation Warnings:</div>
                    <ul className="list-disc list-inside text-yellow-300/80 text-xs space-y-1">
                      {previewData.validation.errors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-white/60">
                <div className="mb-2">⚠️ Game object not generated. Check errors below.</div>
                {previewData.errors && previewData.errors.length > 0 && (
                  <ul className="list-disc list-inside text-red-400 text-sm space-y-1">
                    {previewData.errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          {/* Screenshots */}
          {editableScreenshots && editableScreenshots.length > 0 && (
            <div className="bg-dark-card border border-dark-border rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">
                  Screenshots ({editableScreenshots.length})
                </h3>
                <div className="text-white/40 text-xs">
                  Drag to reorder • Click X to remove
                </div>
              </div>
              <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <div className="text-blue-400 text-sm font-medium">
                  📸 Image #1 will be used as the cover image
                </div>
              </div>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={editableScreenshots}>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {editableScreenshots.map((url, index) => (
                      <SortableScreenshotItem
                        key={url}
                        id={url}
                        url={url}
                        index={index}
                        isCover={index === 0}
                        onRemove={() => handleRemoveScreenshot(index)}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          )}

          {/* Creator Information */}
          {previewData.creator && (
            <div className="bg-dark-card border border-dark-border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Creator</h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <div>
                    <div className="text-white/60 text-sm mb-1">Name</div>
                    <div className="text-white font-medium">{previewData.creator.name}</div>
                  </div>
                  {previewData.creator.isNew && (
                    <div className="text-yellow-400 text-sm">
                      ⚠️ New creator will be created
                    </div>
                  )}
                </div>
                
                {/* Links */}
                <div className="space-y-2 pt-2 border-t border-dark-border">
                  {previewData.creator.itchLink && (
                    <div>
                      <div className="text-white/60 text-sm mb-1">Itch.io</div>
                      <a
                        href={previewData.creator.itchLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-accent-blue hover:underline text-sm"
                      >
                        {previewData.creator.itchLink}
                      </a>
                    </div>
                  )}
                  {previewData.creator.patreonLink && (
                    <div>
                      <div className="text-white/60 text-sm mb-1">Patreon</div>
                      <a
                        href={previewData.creator.patreonLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-accent-blue hover:underline text-sm"
                      >
                        {previewData.creator.patreonLink}
                      </a>
                    </div>
                  )}
                  {previewData.creator.subscribestarLink && (
                    <div>
                      <div className="text-white/60 text-sm mb-1">SubscribeStar</div>
                      <a
                        href={previewData.creator.subscribestarLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-accent-blue hover:underline text-sm"
                      >
                        {previewData.creator.subscribestarLink}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Errors from scraping */}
          {previewData.errors && previewData.errors.length > 0 && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
              <div className="text-yellow-400 text-sm font-medium mb-2">Scraping Warnings:</div>
              <ul className="list-disc list-inside text-yellow-300/80 text-xs space-y-1">
                {previewData.errors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Confirm Button */}
          <div className="flex gap-4">
            <button
              onClick={handleConfirm}
              disabled={confirming || !previewData.gameObject || (previewData.validation && !previewData.validation.valid)}
              className="flex-1 px-6 py-3 bg-green-500/20 border border-green-500/30 rounded-xl text-green-400 font-medium hover:bg-green-500/30 hover:border-green-500/40 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {confirming ? 'Adding Game...' : 'Confirm & Add Game'}
            </button>
            <button
              onClick={() => {
                setPreviewData(null);
                setError(null);
                setSuccess(null);
              }}
              className="px-6 py-3 bg-dark-bg border border-dark-border rounded-xl text-white/60 font-medium hover:border-white/20 hover:text-white/80 transition-all duration-200"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SortableScreenshotItem({ id, url, index, isCover, onRemove }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group ${isCover ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-dark-card' : ''}`}
    >
      <img
        src={url}
        alt={isCover ? `Cover Image` : `Screenshot ${index + 1}`}
        className="w-full h-32 object-cover rounded-lg border border-dark-border"
        onError={(e) => {
          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23333" width="200" height="200"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="14" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EImage%3C/text%3E%3C/svg%3E';
        }}
      />
      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex flex-col items-center justify-center gap-2">
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-white text-xs px-2 py-1 bg-accent-blue/80 rounded"
        >
          ⋮⋮ Drag
        </div>
        {/* Remove Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="text-white text-xs px-2 py-1 bg-red-500/80 hover:bg-red-500 rounded"
        >
          ✕ Remove
        </button>
        {/* View Full Link */}
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-white text-xs px-2 py-1 bg-accent-blue/80 rounded"
        >
          View Full
        </a>
      </div>
      {/* Cover Badge */}
      {isCover && (
        <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded">
          COVER
        </div>
      )}
      {/* Index Badge */}
      <div className={`absolute ${isCover ? 'top-2 right-2' : 'top-2 left-2'} bg-black/70 text-white text-xs px-2 py-1 rounded`}>
        #{index + 1}
      </div>
    </div>
  );
}

export default GameScraper;

